import { IEditorV3 } from "@azu-business/editor-v3";
import {
  AibBlockLine,
  AioExternalSingle,
  AitTableData,
  ArsListingBody,
  ArsPageByItem,
  ArsTableBody,
  FigureDataDto,
  MosaicTrafficLightSwitch,
} from "aic";
import axios, { AxiosError, AxiosResponse, HttpStatusCode } from "axios";
import { AnnotationWhereGroup } from "components/annotations/AnnotationWhereGroup";
import { EditorWithAnnotation } from "components/annotations/interface";
import { v1FigureBody } from "components/tlfpage/V1Figure";
import { isValidUriString } from "functions/isValidUriString";
import { VariantTemplate } from "functions/tlf/isVariantTemplate";
import { Patch } from "rfc6902";
import {
  eHierarchy,
  eHierarchyLevel,
  eLinkType,
  eReleaseStatus,
  eTlfItemType,
  eTlfOutputType,
} from "../enums";
import { HierarchyReadDto } from "../hierarchy/HierarchyReadDto";
import { HierarchyUpdateDto } from "../hierarchy/saveHierarchyData";
import { IAxiosReturn, IAxiosReturnFail } from "../interface";
import { processAxiosError } from "../processAxiosError";
import { ITlfItemRelationship, TlfTag } from "./TlfItemIndexReadDto";
import { isArsMasterReadDto, TlfItemReadDto } from "./TlfItemReadDto";
import { tlfPreProcess } from "./tlfPreProcess";

/**
 * Relationship reference included when creating a TLF item.
 */
export interface TlfItemRelationshipsDto {
  id: number;
  linkId: string;
  linkType: eLinkType;
  azsolId: string;
  version: number;
}

/**
 * Identifies the source or base version of a TLF item.
 */
export interface TlfLookupDto {
  id: string;
  azsolId: string;
  version: number;
  tlfReleaseStatus?: eReleaseStatus;
}

/**
 * Editable content and ownership details used to create a TLF item.
 */
export interface TlfItemCreateDto {
  azsolId: string;
  additionalInformation: string;
  type: eTlfItemType;
  domain: string;
  crystalReference: string;
  programName: string;
  suffix: string;
  outputName: string;
  outputType: eTlfOutputType;
  sapReference: string;
  standardsNotes: IEditorV3;
  programmerNotes: IEditorV3;
  titles: AibBlockLine<IEditorV3>[];
  pageBy: EditorWithAnnotation<AnnotationWhereGroup> | ArsPageByItem<IEditorV3>[];
  body:
    | AitTableData<IEditorV3>
    | ArsListingBody<IEditorV3>
    | ArsTableBody<IEditorV3>
    | v1FigureBody
    | FigureDataDto;
  figureData?: FigureDataDto;
  footnotes: AibBlockLine<IEditorV3>[];
  singles: AioExternalSingle<IEditorV3>[];
  primaryHierarchyId: string;
  relationships: TlfItemRelationshipsDto[];
  tocOrder?: number;
  tocNumber?: string;
  copiedFromTlfLookup?: TlfLookupDto;
  releaseDate?: string;
  basedOnTlfLookup?: TlfLookupDto;
  shellMinorVersion?: number;
  copiedFromHierarchyLevel?: eHierarchyLevel;
  tlfTags?: TlfTag[];
  analysisSetId?: string;
  allVariantPatches?: Record<string, Patch>;
  variantTemplates?: VariantTemplate[];
}

/**
 * Editable TLF fields sent inside the combined update request.
 */
export interface TlfItemUpdateDto {
  additionalInformation: string;
  type: eTlfItemType;
  domain: string;
  crystalReference: string;
  coreTrafficLightSwitch?: MosaicTrafficLightSwitch;
  programName: string;
  suffix: string;
  outputName: string;
  outputType: eTlfOutputType;
  sapReference: string;
  standardsNotes: IEditorV3;
  programmerNotes: IEditorV3;
  titles: AibBlockLine<IEditorV3>[];
  pageBy: EditorWithAnnotation<AnnotationWhereGroup> | ArsPageByItem<IEditorV3>[];
  body:
    | AitTableData<IEditorV3>
    | ArsListingBody<IEditorV3>
    | ArsTableBody<IEditorV3>
    | v1FigureBody
    | FigureDataDto;
  footnotes: AibBlockLine<IEditorV3>[];
  singles: AioExternalSingle<IEditorV3>[];
  shellMinorVersion?: number;
  tocLabel?: string;
  tocNumber?: string;
  analysisSetId?: string;
  allVariantPatches?: Record<string, Patch>;
  variantTemplates?: VariantTemplate[];
}

/**
 * Updates a TLF item and optionally its hierarchy in one backend transaction.
 */
export type TlfItemSaveUpdateDto =
  | {
      tlfItem: TlfItemUpdateDto;
      expectedTlfItemLastUpdatedAt: string;
      hierarchy?: undefined;
      expectedHierarchyLastUpdatedAt?: never;
    }
  | {
      tlfItem: TlfItemUpdateDto;
      expectedTlfItemLastUpdatedAt: string;
      hierarchy: HierarchyUpdateDto;
      expectedHierarchyLastUpdatedAt: string;
    };

/**
 * Response returned by the back end when a TLF item is updated.
 * Updates always return the owning hierarchy, even without hierarchy changes.
 */
export interface TlfItemSaveReadDto {
  tlfItem: TlfItemReadDto;
  hierarchy: HierarchyReadDto;
}

/**
 * Processed result of a TLF item save, containing the saved TLF item and,
 * for updates, its hierarchy. Creation retains the plain TLF response contract.
 */
export interface TlfItemSaveResult {
  tlfItem: TlfItemReadDto;
  hierarchy?: HierarchyReadDto;
}

/**
 * Success or failure result returned by `saveTlfItem`, wrapping the saved TLF item and optional hierarchy.
 */
export type ITlfItemSaveReturn = IAxiosReturn<TlfItemSaveResult>;

/**
 * TLF content, optional creation details and optional transactional hierarchy update.
 */
interface SaveTlfItemBaseDetails {
  tlfItem: TlfItemReadDto;
  newDetails?: {
    azsolId: string;
    version: number;
    domain: string;
    additionalInformation: string;
    primaryHierarchyId: string;
  };
  copiedFromHierarchyLevel?: eHierarchyLevel;
}

/**
 * Details for creating a TLF item or updating it with the expected document versions.
 */
export type SaveTlfItemDetails = SaveTlfItemBaseDetails &
  (
    | { hierarchy?: undefined; expectedHierarchyLastUpdatedAt?: never }
    | { hierarchy: HierarchyUpdateDto; expectedHierarchyLastUpdatedAt: string }
  );

/**
 * Identifies a save rejected because one of its source documents has changed.
 */
export const SAVE_TLF_CONFLICT_ERROR =
  "Save conflict: the TLF item or hierarchy changed on the server. Refreshed data is loaded; review the unsaved draft before saving again.";

/**
 * Save or update a TLF item depending on provided details.
 * When updating, an optional hierarchy can be saved in the same request and is returned with the TLF item.
 *
 * @param backEndUrl - Base backend URL
 * @param saveDetails - Details including the TLF item, optional creation info and optional hierarchy update
 * @param controller - AbortController for cancelling the request
 * @returns Saved or updated TLF item, and any saved hierarchy, wrapped in ITlfItemSaveReturn
 */
export const saveTlfItem = async (
  backEndUrl: string,
  saveDetails: SaveTlfItemDetails,
  controller: AbortController,
): Promise<ITlfItemSaveReturn> => {
  try {
    const tlfItem = saveDetails.tlfItem;
    const basedOnTlfLookup: TlfLookupDto = { id: "", azsolId: "", version: -1 };
    // Only ARS masters carry variant data
    const variantDetails = isArsMasterReadDto(tlfItem)
      ? { allVariantPatches: tlfItem.allVariantPatches, variantTemplates: tlfItem.variantTemplates }
      : {};

    if (saveDetails.tlfItem.relationships.some((r) => r.linkType === eLinkType.BasedOn)) {
      const basedOnLink = saveDetails.tlfItem.relationships.filter(
        (r) => r.linkType === eLinkType.BasedOn,
      )[0];
      basedOnTlfLookup.azsolId = basedOnLink.azsolId;
      basedOnTlfLookup.version = basedOnLink.version;
      basedOnTlfLookup.id = basedOnLink.linkId;
    }

    // Create
    if (saveDetails.newDetails) {
      const newDetails = saveDetails.newDetails;
      // Get template reference
      const newRels: ITlfItemRelationship[] = [];
      const copiedFromTlfLookup: TlfLookupDto = { id: "", azsolId: "", version: -1 };

      copiedFromTlfLookup.id = saveDetails.tlfItem.id;
      copiedFromTlfLookup.azsolId = saveDetails.tlfItem.azsolId;
      copiedFromTlfLookup.version = saveDetails.tlfItem.version;

      const newTlfItem: TlfItemCreateDto = {
        azsolId: newDetails.azsolId,
        additionalInformation: newDetails.additionalInformation,
        type: tlfItem.type,
        domain: newDetails.domain,
        crystalReference: tlfItem.crystalReference ?? "",
        programName: tlfItem.programName ?? "",
        suffix: tlfItem.suffix ?? "",
        outputName: tlfItem.outputName ?? "",
        outputType: tlfItem.outputType ?? eTlfOutputType.Rtf,
        sapReference: tlfItem.sapReference,
        standardsNotes: tlfItem.standardsNotes,
        programmerNotes: tlfItem.programmerNotes,
        titles: tlfItem.titles,
        pageBy: tlfItem.pageBy,
        body: tlfItem.body,
        footnotes: tlfItem.footnotes,
        singles: tlfItem.singles,
        primaryHierarchyId: newDetails.primaryHierarchyId,
        relationships: newRels,
        tocOrder: tlfItem.tocOrder,
        tocNumber: tlfItem.tocNumber,
        copiedFromTlfLookup: copiedFromTlfLookup,
        basedOnTlfLookup: basedOnTlfLookup,
        copiedFromHierarchyLevel: saveDetails.copiedFromHierarchyLevel,
        tlfTags: tlfItem.tlfTags?.map((tag) => ({
          ...tag,
          tagId: tag.tagId ? tag.tagId : crypto.randomUUID(),
        })),
        analysisSetId: tlfItem.analysisSetId,
        ...variantDetails,
      };
      const res: AxiosResponse<TlfItemReadDto> = await axios.post(
        `${backEndUrl}TlfItems`,
        newTlfItem,
        { signal: controller.signal },
      );
      return { success: true, data: { tlfItem: tlfPreProcess(res.data) } };
    }
    // Update
    else {
      // Update body depending on type
      const newBody = { ...tlfItem.body };
      if (tlfItem.type === eTlfItemType.table || tlfItem.type === eTlfItemType.listing) {
        (newBody as AitTableData<IEditorV3>).changedColumns = [];
        (newBody as AitTableData<IEditorV3>).changedRowGroups = [];
      }
      const updateTlfItem: TlfItemUpdateDto = {
        additionalInformation: tlfItem.additionalInformation,
        type: tlfItem.type,
        domain: tlfItem.domain,
        crystalReference: tlfItem.crystalReference ?? "",
        coreTrafficLightSwitch: tlfItem.coreTrafficLightSwitch,
        programName: tlfItem.programName ?? "",
        suffix: tlfItem.suffix ?? "",
        outputName: tlfItem.outputName ?? "",
        outputType: tlfItem.outputType ?? eTlfOutputType.Rtf,
        sapReference: tlfItem.sapReference,
        standardsNotes: tlfItem.standardsNotes,
        programmerNotes: tlfItem.programmerNotes,
        titles: tlfItem.titles,
        pageBy: tlfItem.pageBy,
        body: newBody,
        footnotes: tlfItem.footnotes,
        singles: tlfItem.singles,
        shellMinorVersion:
          tlfItem.primaryHierarchyType === eHierarchy.Report &&
          tlfItem.shellMinorVersion !== undefined
            ? tlfItem.shellMinorVersion + 1
            : tlfItem.shellMinorVersion,
        analysisSetId: tlfItem.analysisSetId,
        ...variantDetails,
      };
      const update: TlfItemSaveUpdateDto = saveDetails.hierarchy
        ? {
            tlfItem: updateTlfItem,
            hierarchy: saveDetails.hierarchy,
            expectedTlfItemLastUpdatedAt: tlfItem.lastUpdatedFromHistory,
            expectedHierarchyLastUpdatedAt: saveDetails.expectedHierarchyLastUpdatedAt,
          }
        : {
            tlfItem: updateTlfItem,
            expectedTlfItemLastUpdatedAt: tlfItem.lastUpdatedFromHistory,
          };
      const res: AxiosResponse<TlfItemSaveReadDto> = await axios.put(
        `${backEndUrl}TlfItems/${tlfItem.id}${isValidUriString(tlfItem.azsolId) ? `/${tlfItem.azsolId}` : ""}`,
        update,
        { signal: controller.signal },
      );
      return {
        success: true,
        data: {
          tlfItem: tlfPreProcess(res.data.tlfItem),
          hierarchy: res.data.hierarchy,
        },
      };
    }
  } catch (error: unknown) {
    if ((error as AxiosError).response?.status === HttpStatusCode.Conflict) {
      const conflict: IAxiosReturnFail = { success: false, errorText: SAVE_TLF_CONFLICT_ERROR };
      return conflict;
    }
    return processAxiosError("Save failed", error);
  }
};
